import { Button, Message } from '@alifd/next';
import { Box } from '@mui/material';
import axios from 'axios';
import moment from 'moment';
import React, { useEffect, useState } from 'react';

import PageHeader from '@/components/PageHeader';
import client from '@/lib/feathers';
import type { ChargingPileElectricityRate } from '@/types/database/chargingPile/chargingPileElectricityRate';

import ElectricityPriceSetting from './components/ElectricityPriceUnitSetting';
import styles from './index.module.scss';
import downloadChargingPilePriceSetting from './utils/downloadChargingPilePriceSetting';

const ChargingPileSetting: React.FC = () => {
  const [initialValues, setInitialValues] =
    useState<ChargingPileElectricityRate>();
  const [trigger, setTrigger] = useState({});
  const [basePrice, setBasePrice] = useState<number>(1);
  useEffect(() => {
    const getPriceSettingInitialValues = async () => {
      try {
        const { data } = await client
          .service('chargingPileElectricityRate')
          .find({});
        setInitialValues(data[0] || {});
      } catch (e) {
        console.error(e);
      }
    };
    getPriceSettingInitialValues();
    const chargingBasicPrice = async () => {
      const priceUrl = `${
        location.origin
      }/ezcon/api/basicElectricityFee?time=${moment().toISOString()}`;
      const { data } = await axios.get(priceUrl, {
        headers: {
          authorization: localStorage.getItem('feathers-jwt'),
        },
      });
      console.log(data.length);
      setBasePrice(data.length === 0 ? 1 : data.length);
    };
    chargingBasicPrice();
  }, [trigger]);

  const handleDownloadSetting = async () => {
    Message.loading('下載中...');
    try {
      await downloadChargingPilePriceSetting();
      Message.success('下載成功');
    } catch (error) {
      console.error(error);
      Message.error('下載失敗');
    }
  };
  console.log(initialValues);
  return (
    <Box>
      <PageHeader
        title="充電樁設定"
        breadcrumbs={[{ name: '汽車充電樁' }, { name: '充電樁設定' }]}
      />
      <ElectricityPriceSetting setTrigger={setTrigger} />
      <Button style={{ marginLeft: 10 }} onClick={handleDownloadSetting}>
        下載充電樁費率設定
      </Button>
      <Box
        sx={{
          padding: '10px',
          borderRadius: '4px',
          backgroundColor: '#fff',
          marginTop: '10px',
        }}
      >
        <h2 className={styles.settingH2}>
          充電樁待機電流：<span>{initialValues?.standbyCurrent}安培</span>
        </h2>
        <h2 className={styles.settingH2}>
          充電樁無訊號設定：<span>{initialValues?.isVoltageMinutes}分鐘</span>
        </h2>

        <h2 className={styles.settingH2}>
          移除充電時間設定：
          <span>{initialValues?.removeChargingArrayMinutes}分鐘</span>
        </h2>
        <h2 className={styles.settingH2}>
          安全設定：
          <span>
            <br />
            電流：
            {initialValues?.chargeLimitSetting?.currentLimitA
              ? '超過' +
                initialValues.chargeLimitSetting.currentLimitA +
                '安培進行斷電'
              : '限制關閉'}
            <br />
            溫度：
            {initialValues?.chargeLimitSetting?.temperatureMaxC
              ? '超過' +
                initialValues.chargeLimitSetting.temperatureMaxC +
                '度進行斷電'
              : '限制關閉'}{' '}
            <br />
            {initialValues?.chargeLimitSetting?.allOfTrip?.currentLimitA
              ? '電流/溫度：電流超過' +
                initialValues.chargeLimitSetting.allOfTrip.currentLimitA +
                '安培與溫度超過' +
                initialValues.chargeLimitSetting.allOfTrip.temperatureMaxC +
                ' 度進行斷電'
              : '電流/溫度：限制關閉'}
          </span>
        </h2>
        <h2 className={styles.settingH2}>
          手動控制充電樁開關：
          <span>{initialValues?.manualControl ? '開啟' : '關閉'}</span>
        </h2>
        <h2 className={styles.settingH2}>
          上限充電模式：
          <span>
            {initialValues?.chargeLimitMode === 'normal'
              ? '未啟用'
              : initialValues?.chargeLimitMode === 'oneDayLimit'
              ? '一天上限'
              : '特定時段上限'}
          </span>
        </h2>
        {initialValues?.chargeLimitMode === 'oneDayLimit' && (
          <>
            {initialValues?.chargingTimeLimitMins && (
              <h2 className={styles.settingH2}>
                最高上限充電時間：
                <span>{initialValues.chargingTimeLimitMins}分鐘</span>
              </h2>
            )}
            {initialValues?.chargingTimeLimitResetTime && (
              <h2 className={styles.settingH2}>
                每天重置時間：
                <span>{initialValues.chargingTimeLimitResetTime}</span>
              </h2>
            )}
          </>
        )}
        {initialValues?.chargeLimitMode === 'periodLimit' && (
          <>
            {initialValues?.periodLimitSetting.map((item, index) => {
              return (
                <h2 key={index} className={styles.settingH2}>
                  特定時段{index + 1}：
                  <span>
                    {item.startHour}時{item.startMinute}分~{item.endHour}時
                    {item.endMinute}分，上限{item.limitMinute}分鐘
                  </span>
                </h2>
              );
            })}
          </>
        )}
        <h2 className={styles.settingH2}>
          夏季基本電費：
          {initialValues?.summer?.basicPrice &&
            initialValues.summer.basicPrice >= 0 && (
              <span>
                總共{initialValues.summer.basicPrice}元，平均每位分攤
                {Math.ceil(initialValues.summer.basicPrice / basePrice)}元
              </span>
            )}
        </h2>

        <h2 className={styles.settingH2}>
          夏季尖峰每度費用：<span>{initialValues?.summer.peak.price}元</span>
        </h2>
        <h2 className={styles.settingH2}>
          夏季離峰每度費用：<span>{initialValues?.summer.offPeak.price}元</span>
        </h2>

        <h2 className={styles.settingH2}>
          非夏季基本電費：
          {initialValues?.nonSummer.basicPrice &&
            initialValues.nonSummer.basicPrice >= 0 && (
              <span>
                總共{initialValues.nonSummer.basicPrice}元，平均每位分攤
                {Math.ceil(initialValues.nonSummer.basicPrice / basePrice)}元
              </span>
            )}
        </h2>

        <h2 className={styles.settingH2}>
          非夏季尖峰每度費用：
          <span>{initialValues?.nonSummer.peak.price}元</span>
        </h2>
        <h2 className={styles.settingH2}>
          非夏季離峰每度費用：
          <span>{initialValues?.nonSummer.offPeak.price}元</span>
        </h2>
        <h2 className={styles.settingH2}>
          系統異常每度費用：
          <span>{initialValues?.systemShutDownElectricityRate}元</span>
        </h2>
        <h2 className={styles.settingH2}>
          夏季時間：
          <span>
            {moment(initialValues?.summer.dates[0][0], 'MM-DD').format(
              'MM月DD日'
            )}
            ~
            {moment(initialValues?.summer.dates[0][1], 'MM-DD').format(
              'MM月DD日'
            )}
          </span>
        </h2>
        <h2 className={styles.settingH2}>
          夏季尖峰時段：
          {initialValues?.summer.peak.hours.map((item, index) => {
            return (
              <span key={index}>
                <br /> {`${item[0]}~${item[1]}`}
              </span>
            );
          })}
        </h2>
        <h2 className={styles.settingH2}>
          非夏季尖峰時段：
          {initialValues?.nonSummer.peak.hours.map((item, index) => {
            return (
              <span key={index}>
                <br /> {`${item[0]}~${item[1]}`}
              </span>
            );
          })}
        </h2>
      </Box>
    </Box>
  );
};

export default ChargingPileSetting;
